import { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import type { Notificacao } from '../types';
import { notificacoesApi } from '../services/api';
import { Bell, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';

const TIPOS = [
  { value: '', label: 'Todos' },
  { value: 'reserva_confirmacao', label: 'Confirmação de reserva' },
  { value: 'auto_fecho_resumo', label: 'Resumo de fecho automático' },
];

function tipoLabel(tipo: string) {
  return TIPOS.find(t => t.value === tipo)?.label || tipo;
}

function canalLabel(canal: string) {
  return canal === 'email' ? 'Email' : canal === 'sms' ? 'SMS' : canal;
}

function estadoBadge(estado: string) {
  if (estado === 'enviada') return 'badge-success';
  if (estado === 'erro') return 'badge-danger';
  return 'badge-warning';
}

function dataLabel(value: string) {
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm', { locale: pt });
  } catch {
    return value || '—';
  }
}

export function NotificacoesPage() {
  const { error } = useToast();
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selecionada, setSelecionada] = useState<Notificacao | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const response = await notificacoesApi.getAll(page, 50, tipo || undefined);
        if (cancelled) return;
        if (response.success && response.data) {
          setNotificacoes(response.data.items);
          setTotalPages(response.data.totalPages);
          setTotalCount(response.data.totalCount);
        }
      } catch {
        if (!cancelled) error('Erro ao carregar notificações');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [page, tipo, error]);

  const handleTipo = (novo: string) => {
    setLoading(true);
    setTipo(novo);
    setPage(1);
  };

  const changePage = (delta: number) => {
    setLoading(true);
    setPage(p => p + delta);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Notificações</h1>
        <div className="header-actions">
          <div className="filter-group">
            <Bell className="w-4 h-4" />
            <select className="input input-sm" value={tipo} onChange={e => handleTipo(e.target.value)}>
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="page-loading">
            <div className="spinner"></div>
            <p>A carregar notificações...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Canal</th>
                  <th>Destinatário</th>
                  <th>Assunto</th>
                  <th>Estado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {notificacoes.map(n => (
                  <tr key={n.id}>
                    <td>{dataLabel(n.dtCriacao)}</td>
                    <td>{tipoLabel(n.tipo)}</td>
                    <td>{canalLabel(n.canal)}</td>
                    <td>{n.destinatario || '—'}</td>
                    <td>{n.assunto || '—'}</td>
                    <td>
                      <span className={`badge ${estadoBadge(n.estado)}`}>{n.estado}</span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setSelecionada(n)} aria-label="Abrir notificação">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && notificacoes.length === 0 && (
          <div className="empty-state">
            <Bell className="w-12 h-12 text-gray-300" />
            <h3>Nenhuma notificação encontrada</h3>
            <p>Não existem notificações para este filtro</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => changePage(-1)}>
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>
            <span className="pagination-info">
              Página {page} de {totalPages} · {totalCount} notificações
            </span>
            <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => changePage(1)}>
              Seguinte
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <Modal
        title={selecionada ? (selecionada.assunto || 'Notificação') : 'Notificação'}
        open={!!selecionada}
        onClose={() => setSelecionada(null)}
        maxWidth={720}
        footer={<button className="btn btn-secondary" onClick={() => setSelecionada(null)}>Fechar</button>}
      >
        {selecionada && (
          <div>
            <div className="detail-grid" style={{ marginBottom: 12 }}>
              <div className="detail-item">
                <span className="detail-label">Data</span>
                <span className="detail-value">{dataLabel(selecionada.dtCriacao)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Tipo</span>
                <span className="detail-value">{tipoLabel(selecionada.tipo)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Canal</span>
                <span className="detail-value">{canalLabel(selecionada.canal)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Destinatário</span>
                <span className="detail-value">{selecionada.destinatario || '—'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Estado</span>
                <span className="detail-value">
                  <span className={`badge ${estadoBadge(selecionada.estado)}`}>{selecionada.estado}</span>
                </span>
              </div>
              {selecionada.enviadoEm && (
                <div className="detail-item">
                  <span className="detail-label">Enviado em</span>
                  <span className="detail-value">{dataLabel(selecionada.enviadoEm)}</span>
                </div>
              )}
            </div>
            {selecionada.erro && <div className="form-error" role="alert">{selecionada.erro}</div>}
            <div
              style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 16 }}
              dangerouslySetInnerHTML={{ __html: selecionada.corpo }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
